import { useCallback, useRef } from 'react';
import { parseJSON } from '@/shared/commonFunctions';
import { useEvents } from '../use-events/useEvents';
import { useClient } from '@/hooks/ClientContext';
import { useUserContext } from '@/hooks/userContext/UserContext';
import { ensureClientsGroupedByLoja } from '@/utils/clientDataTransform';
import type { InconsistencyProblem } from '@/shared/inconsistenciesData';
import { Tables, toCoded, useDbFunctions, isValidDbPayload, resolveTableNameForWrite } from '../dbFunctions/useDBFunctions';
import { DatasetWithStoreMeta, normalizeDatasetType, _memTune, updateProgress, getDatasetKey } from './_syncCommons';
import {
  mergeByDatasetScope,
  mergeCatalogByProductCode,
  mergeClientsByCodePreserveStores,
  mergePrecoByScope,
  mergePrecoPromocaoClientePartial,
  mergeParametroPrecoBasePartial,
  reducePromocaoClienteArrayToNestedMap,
} from '@/shared/datasetIncrementalMerge';
import {
  extractAtomicFailureTableKey,
  getOnStorage,
  isImageDataset,
  mergeDatasetsByUrl,
  mergeEstoquePreserveExistingStores,
  migrateImageDatasetsFromMain,
  OBRIGATORY_EMPTY_BLOCK_REASON,
  registerSuccessfulDownloads,
  removeFromStorage,
  setOnStorage,
  writeImageDatasets,
} from '@/shared';
import {
  TableTypes,
  CategoryProps,
  DataSetsProps,
  ProductProps,
  CustomerDataProps,
  EventsClientProps,
  DicUltimaCompraProps,
  CampanhaProdutosProps,
  PrecoPromocaoClienteProps,
  ClientProps,
  PageBuilderProps,
  SalesCycleProps,
} from '@/types';

type CatalogDictProps = Record<number, ProductProps>;
type CategoryDictProps = Record<number, CategoryProps>;

const CATALOG_CHUNK_SIZE = _memTune.catalogChunkSize;
const CLIENTS_CHUNK_SIZE = _memTune.clientsChunkSize;

let globalSyncInProgress = false;

const TABLE_DATASET_TYPES: Partial<Record<Tables, string[]>> = {
  preco: ['preco', 'preco_novo'],
  cod_regime_tributario: ['cod_regime_tributario', 'substituicao_tributaria_aws'],
};

const mountCategories = (campanha: CampanhaProdutosProps) => {
  if (!campanha) return [];

  return [
    {
      NRO_HIERARQUIA: 1,
      DE_CATEGORIA: campanha.nome,
      IMAGE: campanha?.inativo?.selo || '',
      PATH_CATEGORIA: campanha.url_amigavel,
    },
  ] as CategoryProps[];
};

const mergeProductPrices = (current: ProductProps['PRECO'], incoming: ProductProps['PRECO']) => {
  const byKey = new Map<string, ProductProps['PRECO'][number]>();
  [...(current || []), ...(incoming || [])].forEach((price) => {
    if (!price) return;
    const key = [
      Number(price.COD_LOJA || 0),
      Number(price.NRO_SEGMENTO || 0),
      Number(price.NROCONDICAOPAGTO || 0),
      Number(price.QTD_EMBALAGEM || 0),
      Number(price.QTD_MULTIPLO_VENDA || 0),
      `${price.SG_EMBALAGEM || ''}`,
      `${price.TP_PROMO || ''}`,
      `${price.DT_INICIO || ''}`,
      `${price.DT_FIM || ''}`,
    ].join(':');
    byKey.set(key, price);
  });
  return Array.from(byKey.values());
};

const mergeProductPackages = (current: ProductProps['EMBALAGEM'], incoming: ProductProps['EMBALAGEM']) => {
  const byKey = new Map<string, ProductProps['EMBALAGEM'][number]>();
  [...(current || []), ...(incoming || [])].forEach((pack) => {
    if (!pack) return;
    const key = [
      Number(pack.COD_LOJA || 0),
      Number(pack.COD_SEGMENTO || 0),
      Number(pack.QTD_EMBALAGEM || 0),
      Number(pack.QTD_MULTIPLO_VENDA || 0),
      `${pack.SG_EMBALAGEM || ''}`,
    ].join(':');
    byKey.set(key, pack);
  });
  return Array.from(byKey.values());
};

const mergeClientLojas = (current: ClientProps['lojas'], incoming: ClientProps['lojas']) => {
  const byKey = new Map<string, NonNullable<ClientProps['lojas']>[number]>();
  [...(current || []), ...(incoming || [])].forEach((loja) => {
    if (!loja) return;
    const segmentos = Array.isArray(loja.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
    const segmentosKey = segmentos
      .map((segmento: any) => Number(segmento?.cod_segmento ?? segmento?.COD_SEGMENTO ?? segmento))
      .filter((n: number): n is number => Number.isFinite(n))
      .join(',');
    const key = `${Number(loja.COD_LOJA || 0)}:${segmentosKey}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, { ...loja });
      return;
    }
    byKey.set(key, { ...prev, ...loja });
  });

  return Array.from(byKey.values());
};

const mergeProductsByCode = (current: ProductProps | undefined, incoming: ProductProps): ProductProps => {
  if (!current) return incoming;

  return {
    ...current,
    ...incoming,
    PRECO: mergeProductPrices(current.PRECO, incoming.PRECO),
    EMBALAGEM: mergeProductPackages(current.EMBALAGEM, incoming.EMBALAGEM),
    ATRIBUTOS: (incoming.ATRIBUTOS?.length ? incoming.ATRIBUTOS : current.ATRIBUTOS) || [],
    IMAGEM: incoming.IMAGEM || current.IMAGEM || '',
    ultimaCompra: incoming.ultimaCompra || current.ultimaCompra || {},
  };
};

const yieldToMain = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      setTimeout(resolve, 0);
    }
  });

const resolveDatasetTypesForTable = (table: Tables): string[] => TABLE_DATASET_TYPES[table] ?? [table];

const wasTableAttemptedInRun = (table: Tables, typesAttempted: Set<string>) =>
  resolveDatasetTypesForTable(table).some((type) => typesAttempted.has(type));


type PrepareFilesProps = {
  isSuccess: boolean;
  preparing: boolean;
  setError: (error: boolean) => void;
  setIsLoaded: (isLoaded: boolean) => void;
  setNoUpdate: (noUpdate: boolean) => void;
  setPreparing: (preparing: boolean) => void;
  setIsSuccess: (isSuccess: boolean) => void;
  setDataSets: (dataSets: DataSetsProps[]) => void;
  setIsOpenedModal: (isOpenedModal: boolean) => void;
  fetchDatasets: (datasets: DataSetsProps[]) => Promise<any>;
  setDownloadingFileName: (downloadingFileName: string) => void;
  setDownloadSpeedBps: (downloadSpeedBps: number) => void;
  setProgressPercent: (progressPercent: number) => void;
};

export const usePrepareFiles = (_useState: PrepareFilesProps) => {
  const loading = useRef(false);
  const downloadLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { setCustomer } = useUserContext();
  const { setOnDB, getOnDB, setMultipleOnDBAtomic, setImagesOnDB } = db;
  const { setCampanhaProdutos, setCampanhaClientes, setCampanhaFaixa, setCampanhaTopMix, setIndicadorLoja, setIndicadoresProdutividade } = useClient();
  
  let isLoaded = false;
  let mapedPrices: any = {};
  let products: ProductProps[] = [];
  let eventos: EventsClientProps = {};
  let sortedClients: ClientProps[] = [];
  let allCategories: CategoryProps[] = [];
  let pageBuilder: PageBuilderProps[] = [];
  const objProduct = {} as CatalogDictProps;
  const objCategory = {} as CategoryDictProps;
  let promocaoCliente: PrecoPromocaoClienteProps = {};
  const productUltimaCompra: DicUltimaCompraProps = {};
  const cycleByClient: Record<number, SalesCycleProps[]> = {};
  const clientes: Record<number, ClientProps & { CYCLE: SalesCycleProps[] }> = {};

  const tryCatch = async <T>(fn: () => Promise<T>, msg: string): Promise<T | undefined> => {
    try {
      await fn();
    } catch (error) {
      void registerEvent({
        tipoEvento: 'PROCESSAMENTO',
        obsEvento: `Erro ao carregar [${msg}]: ${error}`,
        stEvento: 0,
      }).catch(() => {});
      return undefined;
    }
    await yieldToMain();
  };

  const loadFiles = useCallback(
    async (newDatasets?: DataSetsProps[], skipGlobalGuard = false, isBackground = false) => {
      if (!skipGlobalGuard && globalSyncInProgress) return;
      if (!skipGlobalGuard) globalSyncInProgress = true;
      if (loading.current) return;

      loading.current = true;
      _useState.setError(false);
      _useState.setIsSuccess(false);
      updateProgress(0, 0, '.');
      try {
        const listToDownload = (newDatasets || []).map((d) => ({
          ...d,
          TIPO_ARQUIVO: normalizeDatasetType(d.TIPO_ARQUIVO),
        }));
        if (!listToDownload.length) {
          _useState.setNoUpdate(true);
          _useState.setIsSuccess(true);
          return true;
        }

        if (listToDownload.find((i) => i.TIPO_ARQUIVO === 'estoque')) {
          removeFromStorage('stock_left');
        }

        if (!isBackground) {
          _useState.setPreparing(false);
          _useState.setIsOpenedModal(true);
        }

        const resp = await _useState.fetchDatasets(listToDownload);

        void registerEvent({
          tipoEvento: 'PROCESSAMENTO',
          obsEvento: 'Iniciando processamento de dados',
        }).catch(() => {});

        // Pega a lista antiga de datasets (imagens ficam em chave separada)
        const oldDatasets = (getOnStorage('datasets') || []).filter((dataset) => !isImageDataset(dataset));
        const oldImageDatasets = migrateImageDatasetsFromMain();
        // Pega os datasets que foram baixados com sucesso
        const successfullyDownloaded = listToDownload.filter((d, index) => resp?.byDatasetKey?.[getDatasetKey(d, index)]);
        const successfullyDownloadedMain = successfullyDownloaded.filter((dataset) => !isImageDataset(dataset));
        const successfullyDownloadedImages = successfullyDownloaded.filter((dataset) => isImageDataset(dataset));

        // Cria um mapa da lista antiga para fácil substituição
        const oldDatasetsMap = oldDatasets.reduce(
          (acc, item) => {
            acc[getDatasetKey(item)] = item;
            return acc;
          },
          {} as Record<string, DataSetsProps>
        );

        // Atualiza o mapa com os arquivos baixados com sucesso
        successfullyDownloadedMain.forEach((item) => {
          oldDatasetsMap[getDatasetKey(item)] = item;
        });

        // Converte o mapa de volta para uma lista e salva
        const finalDatasetList = Object.values(oldDatasetsMap);
        setOnStorage('datasets', finalDatasetList);
        if (successfullyDownloadedImages.length) {
          writeImageDatasets(mergeDatasetsByUrl(oldImageDatasets, successfullyDownloadedImages));
        }
        _useState.setDataSets(listToDownload);

        _useState.setPreparing(true);

        const datasetsByType = successfullyDownloaded.reduce(
          (acc, dataset) => {
            const type = normalizeDatasetType(dataset.TIPO_ARQUIVO);
            if (!acc[type]) acc[type] = [];
            acc[type].push(dataset as DatasetWithStoreMeta);
            return acc;
          },
          {} as Record<string, DatasetWithStoreMeta[]>
        );

        const finalDatasetsByType = finalDatasetList.reduce(
          (acc, dataset) => {
            const normalized = {
              ...(dataset as DatasetWithStoreMeta),
              TIPO_ARQUIVO: normalizeDatasetType(dataset.TIPO_ARQUIVO),
            } as DatasetWithStoreMeta;
            if (!acc[normalized.TIPO_ARQUIVO]) acc[normalized.TIPO_ARQUIVO] = [];
            acc[normalized.TIPO_ARQUIVO].push(normalized);
            return acc;
          },
          {} as Record<string, DatasetWithStoreMeta[]>
        );

        const hadImagensDownloadInThisRun = successfullyDownloaded.some(
          (d) => normalizeDatasetType(d.TIPO_ARQUIVO) === 'imagens'
        );

        const getData = async <T extends Tables>(dbKey: T, defaultValue: any): Promise<TableTypes[T]> => {
          const key = dbKey === 'dicionario_produtos' ? 'catalogo' : dbKey;
          const incoming = resp?.byType?.[key]?.data;
          if (incoming == null) {
            const fromDb = await getOnDB(dbKey, undefined, undefined, dbKey === 'imagens', false);
            return (fromDb ?? defaultValue) as TableTypes[T];
          }


          const updatedDatasets = datasetsByType[dbKey] || [];
          const expectedDatasets = finalDatasetsByType[dbKey] || [];
          const isPartialSync = expectedDatasets.length > updatedDatasets.length;
          if (!isPartialSync) return incoming as TableTypes[T];

          const fromDb = await getOnDB(dbKey, undefined, undefined, dbKey === 'imagens', false);
          if (dbKey === 'cliente') {
            return mergeClientsByCodePreserveStores(fromDb, incoming) as TableTypes[T];
          }
          
          if (dbKey === 'dicionario_produtos') {
            const data = fromDb ? Object.values(fromDb) : [];
            const result = mergeCatalogByProductCode(Object.values(data || {}), incoming) as TableTypes[T];
            return result;
          }
          if (dbKey === 'precoPromocaoCliente') {
            return mergePrecoPromocaoClientePartial(fromDb, incoming) as TableTypes[T];
          }
          if (dbKey === 'parametro_preco_base') {
            return mergeParametroPrecoBasePartial(fromDb, incoming) as TableTypes[T];
          }

          return mergeByDatasetScope(fromDb, incoming, updatedDatasets) as TableTypes[T];
        };

        const getEstoqueData = async (): Promise<TableTypes['estoque']> => {
          const fromDb = await getOnDB('estoque');
          const incoming = resp?.byType?.estoque?.data;
          if (incoming == null) {
            return (Array.isArray(fromDb) ? fromDb : []) as TableTypes['estoque'];
          }
          return mergeEstoquePreserveExistingStores(fromDb, incoming) as TableTypes['estoque'];
        };

        const getPrecoData = async () => {
          // Prioriza novo dataset de preço e mantém fallback para o legado.
          if (resp?.byType?.preco_novo?.data != null) {
            const incoming = resp.byType.preco_novo.data;
            const updatedDatasets = datasetsByType.preco_novo || [];
            const expectedDatasets = finalDatasetsByType.preco_novo || [];
            const isPartialSync = expectedDatasets.length > updatedDatasets.length;
            if (!isPartialSync) return incoming;

            const fromDb = await getOnDB('preco');
            return mergePrecoByScope(fromDb, incoming, updatedDatasets);
          }
          return getData('preco', []);
        };

        const parseNumberLoose = (value: any): number | null => {
          if (value === null || value === undefined) return null;
          if (typeof value === 'number') return Number.isFinite(value) ? value : null;
          if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return null;
            // Tenta lidar com valores com ',' (pt-BR) e '.' (milhares)
            const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;
            const num = Number(normalized);
            return Number.isFinite(num) ? num : null;
          }
          return null;
        };

        const parseJSONIfString = <T = any>(value: unknown, fallback: string): T => {
          if (typeof value === 'string') {
            return parseJSON(value || fallback) as T;
          }

          if (value === null || value === undefined) {
            return parseJSON(fallback) as T;
          }

          return value as T;
        };

        // Saldo flex (novo formato):
        // { [COD_PESSOA]: { VLR_ERP } }
        const mapSaldoFlexToNewMap = (src: any): Record<string, { VLR_ERP: number | null }> => {
          if (!src) return {};

          // Alguns backends podem trazer wrapper: { data: {...} } ou { data: { data: {...} } }
          const candidates: any[] = [src, src?.data, src?.data?.data].filter(Boolean);
          const mapRoot = candidates.find((c) => {
            if (!c || typeof c !== 'object' || Array.isArray(c)) return false;
            const keys = Object.keys(c);
            return keys.length > 0 && keys.some((k) => Number.isFinite(Number(k)));
          });

          if (!mapRoot) return {};

          return Object.entries(mapRoot).reduce<Record<string, { VLR_ERP: number | null }>>((acc, [cod, node]) => {
            const codPessoa = Number(cod);
            if (!Number.isFinite(codPessoa)) return acc;

            // novo formato: { [COD_PESSOA]: { VLR_ERP } }
            // tolera também caso venha { [COD_PESSOA]: { VLR_ERP: '...' } } ou valor direto
            const vlrRaw = node && typeof node === 'object' ? ((node as any).VLR_ERP ?? node) : null;

            const parsed = parseNumberLoose(vlrRaw);
            acc[String(codPessoa)] = { VLR_ERP: parsed };
            return acc;
          }, {});
        };

        const getSaldoFlexData = async (): Promise<TableTypes['saldo_flex']> => {
          // Prioriza o novo dataset, e salva diretamente no `saldo_flex` (objeto por COD_PESSOA).
          // Não suportamos mais o formato legado (array).
          const srcNovo = resp?.byType?.saldo_flex_novo?.data;
          if (srcNovo != null) return mapSaldoFlexToNewMap(srcNovo);

          const srcFlex = resp?.byType?.saldo_flex?.data;
          if (srcFlex != null && !Array.isArray(srcFlex)) return mapSaldoFlexToNewMap(srcFlex);

          const fromDb = await getOnDB('saldo_flex');
          if (!fromDb || Array.isArray(fromDb)) return {};
          return mapSaldoFlexToNewMap(fromDb);
        };

        const [
          precos,
          marcas,
          imagens,
          estoque,
          catalogo,
          saldoFlex,
          loadedClients,
          categorias,
          clienteBoletos,
          clienteEventosCortes,
          indicadores,
          campanha,
          listPageBuilder,
          janelaEntrega,
          parametrosAws,
          indicadorLoja,
          campanhaFaixa,
          campanhaProduto,
          campanhaCliente,
          campanhaTopMix,
          parametroPrecoBase,
          recomendacaoBigData,
          precoPromocaoCliente,
          cicloVendaCliente,
          percentualFornecedor,
          substituicaotributaria,
          restricaoProdutoCliente,
          ultimaCompra,
          combo,
        ] = await Promise.all([
          getPrecoData(),
          getData('marcas', []),
          hadImagensDownloadInThisRun ? getData('imagens', {}) : Promise.resolve({} as TableTypes['imagens']),
          getEstoqueData(),
          getData('dicionario_produtos', {}).then((data) => data as ProductProps[]),
          getSaldoFlexData(),
          getData('cliente', []),
          getData('categoria', []),
          getData('cliente_boletos', {}),
          getData('cliente_cortes', {}),
          getData('indicadores', []),
          getData('campanha_indicador', []),
          getData('pagebuilder', []),
          getData('janela_entrega', []),
          getData('parametros_aws', []),
          getData('indicador_loja', []),
          getData('campanha_faixa', []),
          getData('campanha_produto', {}),
          getData('campanha_cliente', {}),
          getData('campanha_mix_fornecedor', []),
          getData('parametro_preco_base', []),
          getData('recomendacaoBigData', []),
          getData('precoPromocaoCliente', []),
          getData('ciclo_venda_cliente_cliente', []),
          getData('percentual_fornecedor', []),
          getData('cod_regime_tributario', []),
          getData('restricaoProdutoCliente', []),
          getData('ultima_compra', []),
          getData('combo', []),
        ]);

        // Define origem dos dados de preço:
        // - prioriza dataset preco_novo
        // - fallback para preço legado salvo no DB
        const precosSource = precos ?? [];

        const yieldToMain = (): Promise<void> =>
          new Promise((resolve) => {
            if (typeof requestIdleCallback !== 'undefined') {
              requestIdleCallback(() => resolve(), { timeout: 50 });
            } else {
              setTimeout(resolve, 0);
            }
          });

        // Trabalhando com o dataset de eventos do cliente [clienteBoletos]
        await tryCatch(async () => {
          eventos =
            clienteBoletos?.clientes?.reduce((acc, cliente) => {
              return {
                ...acc,
                [cliente.cod_cliente]: {
                  boletos: cliente.boletos,
                  cod_cliente: cliente.cod_cliente,
                },
              };
            }, {} as EventsClientProps) || {};
        }, 'clienteBoletos');

        // Trabalhando com o dataset de ciclo de venda do cliente [cicloVendaCliente]
        await tryCatch(async () => {
          if (Array.isArray(cicloVendaCliente)) {
            for (let j = 0; j < cicloVendaCliente.length; j++) {
              const cycle = cicloVendaCliente[j];
              const cod = cycle.COD_CLIENTE;
              if (!cycleByClient[cod]) cycleByClient[cod] = [];
              cycleByClient[cod].push(cycle);
            }
            Object.keys(cycleByClient).forEach((cod) => {
              cycleByClient[Number(cod)].sort((a, b) => a.SEMANA_CICLO_ID - b.SEMANA_CICLO_ID);
            });
          }
        }, 'cicloVendaCliente');

        // Trabalhando com o dataset de clientes [loadedClients]
        await tryCatch(async () => {
          const grouped = ensureClientsGroupedByLoja(loadedClients || []);
          sortedClients = grouped.sort((a, b) => {
            if (a.NM_FANTASIA > b.NM_FANTASIA) return 1;
            if (a.NM_FANTASIA < b.NM_FANTASIA) return -1;
            return 0;
          });
        }, 'loadedClients');

        // Trabalhando com o dataset de clientes [clientes]
        await tryCatch(async () => {
          for (let i = 0; i < sortedClients.length; i += CLIENTS_CHUNK_SIZE) {
            const chunk = sortedClients.slice(i, i + CLIENTS_CHUNK_SIZE);
            chunk.forEach((c) => {
              c.COD_SEGMENTO = parseJSONIfString(c?.COD_SEGMENTO, '[]');
              c.DT_ULTIMA_COMPRA = parseJSONIfString(c?.DT_ULTIMA_COMPRA, '[]');
              c.UNILEVER = c.FL_UNILEVER === 'True';
              const CYCLE = cycleByClient[c.COD_CLIENTE] || [];
              const currentClient = clientes[c.COD_CLIENTE];
              if (!currentClient) {
                clientes[c.COD_CLIENTE] = { ...c, CYCLE };
                return;
              }

              const mergedLojas = mergeClientLojas(currentClient.lojas, c.lojas);
              clientes[c.COD_CLIENTE] = {
                ...currentClient,
                ...c,
                lojas: mergedLojas,
                CYCLE: CYCLE.length ? CYCLE : currentClient.CYCLE,
              };
            });
            await yieldToMain();
          }
        }, 'clientes');

        // Trabalhando com o dataset de preços [precos]
        await tryCatch(async () => {
          const src: any = precosSource;
          if (!src || typeof src !== 'object') {
            mapedPrices = {};
            return;
          }

          const isLikelyPriceItem = (v: any) => {
            if (!v || typeof v !== 'object') return false;
            // Campos mais comuns no payload de preço
            return (
              v.VLR_PRECO != null ||
              v.VLR_PAUTA != null ||
              v.DESTACA_ST != null ||
              v.COD_TRIBUTACAO_PRODUTO != null ||
              v.COD_PROMOCAO != null
            );
          };

          const out: Record<string, any[]> = {};

          // Formato antigo:
          //   { PRODUTOS: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: PriceItem } }, FILTRO: {...} }
          if (src.PRODUTOS && typeof src.PRODUTOS === 'object') {
            const produtos = src.PRODUTOS || {};
            const filtro = src.FILTRO || {};

            Object.entries(produtos).forEach(([codProd, embalagens]) => {
              out[codProd] = out[codProd] || [];
              Object.entries(embalagens as any).forEach(([qtdEmb, preco]) => {
                const precoItem = preco as any;
                out[codProd].push({
                  ...precoItem,
                  COD_PRODUTO: Number(codProd),
                  QTD_EMBALAGEM: Number(qtdEmb),
                  COD_LOJA: filtro.COD_LOJA ?? null,
                  NRO_SEGMENTO: filtro.NRO_SEGMENTO ?? null,
                  TP_PROMO: filtro.TP_PROMO ?? precoItem.TP_PROMO ?? null,
                  FAIXAACRFINANCEIRO: filtro.FAIXAACRFINANCEIRO ?? precoItem.FAIXAACRFINANCEIRO ?? null,
                  INDUSAACRESCTABVENDA: filtro.INDUSAACRESCTABVENDA ?? precoItem.INDUSAACRESCTABVENDA ?? null,
                });
              });
            });

            mapedPrices = out;
            return;
          }

          // (exemplo do usuário):
          // { data: { [COD_LOJA]: { [NRO_SEGMENTO]: { [COD_PRODUTO]: { [QTD_EMBALAGEM]: PriceItem } } } } }
          // Além disso, o dataset pode vir com wrapper tipo: { data: {...}, keyPath: 'preco', preco: 1 }
          const candidates: any[] = [src.data, src.data?.data, src];
          const dataRoot = candidates.find((c) => {
            if (!c || typeof c !== 'object') return false;
            const keys = Object.keys(c);
            return keys.some((k) => !Number.isNaN(Number(k)) && k !== 'keyPath' && k !== 'preco');
          });

          if (!dataRoot || typeof dataRoot !== 'object') {
            mapedPrices = {};
            return;
          }

          Object.entries(dataRoot as any).forEach(([codLoja, bySegment]) => {
            if (!bySegment || typeof bySegment !== 'object') return;

            Object.entries(bySegment as any).forEach(([nroSegmento, byProduct]) => {
              if (!byProduct || typeof byProduct !== 'object') return;

              Object.entries(byProduct as any).forEach(([codProd, priceNode]) => {
                if (!priceNode || typeof priceNode !== 'object') return;

                const codLojaNum = Number(codLoja);
                const segNum = Number(nroSegmento);
                if (!Number.isFinite(codLojaNum) || !Number.isFinite(segNum)) return;

                // priceNode costuma ser { [QTD_EMBALAGEM]: PriceItem }
                const embEntries = Object.entries(priceNode as any);
                if (embEntries.length === 0) return;

                const [firstKey] = embEntries[0];
                const firstKeyNum = Number(firstKey);

                // No dataset novo, as chaves de priceNode são quantidades de embalagem.
                if (!Number.isNaN(firstKeyNum)) {
                  embEntries.forEach(([qtdEmb, preco]) => {
                    const qtdEmbNum = Number(qtdEmb);
                    if (Number.isNaN(qtdEmbNum) || !preco) return;

                    out[codProd] = out[codProd] || [];
                    out[codProd].push({
                      ...(preco as any),
                      COD_PRODUTO: Number(codProd),
                      QTD_EMBALAGEM: qtdEmbNum,
                      COD_LOJA: codLojaNum,
                      NRO_SEGMENTO: segNum,
                    });
                  });
                } else if (isLikelyPriceItem(priceNode)) {
                  // Caso raro: priceNode já seja um item único (não mapeado por QTD)
                  const qtdEmbNum =
                    (priceNode as any).QTD_EMBALAGEM != null
                      ? Number((priceNode as any).QTD_EMBALAGEM)
                      : (priceNode as any).QTD != null
                        ? Number((priceNode as any).QTD)
                        : null;

                  if (qtdEmbNum != null && Number.isFinite(qtdEmbNum)) {
                    out[codProd] = out[codProd] || [];
                    out[codProd].push({
                      ...(priceNode as any),
                      COD_PRODUTO: Number(codProd),
                      QTD_EMBALAGEM: qtdEmbNum,
                      COD_LOJA: codLojaNum,
                      NRO_SEGMENTO: segNum,
                    });
                  }
                }
              });
            });
          });

          mapedPrices = out;
        }, 'precos');

        // Trabalhando com o dataset de preços promocionais [precoPromocaoCliente]
        await tryCatch(async () => {
          promocaoCliente = Array.isArray(precoPromocaoCliente)
            ? reducePromocaoClienteArrayToNestedMap(precoPromocaoCliente)
            : precoPromocaoCliente;
        }, 'precoPromocaoCliente');

        // Trabalhando com o dataset de última compra [ultimaCompra]
        await tryCatch(async () => {
          if (Array.isArray(ultimaCompra)) {
            for (let j = 0; j < ultimaCompra.length; j++) {
              const c = ultimaCompra[j];
              productUltimaCompra[c.SEQPRODUTO] = c;
            }
          }
        }, 'ultimaCompra');

        // Trabalhando com o dataset de produtos [catalogo]
        await tryCatch(async () => {
          for (let i = 0; i < catalogo.length; i += CATALOG_CHUNK_SIZE) {
            const chunk = catalogo.slice(i, i + CATALOG_CHUNK_SIZE);
            chunk.forEach((item: ProductProps) => {
              // Inclui todo o catálogo no dicionário; preços podem faltar se o dataset de preço
              // vier vazio ou em formato não mapeado — nesse caso PRECO fica [] (evita obj vazio no IDB).
              const PRECO = mapedPrices[item.COD_PRODUTO] ?? mapedPrices[String(item.COD_PRODUTO)] ?? [];

              const EMBALAGEM = item?.EMBALAGEM?.map((emb) => {
                if (emb?.QTD_MULTIPLO_VENDA > 1) {
                  return { ...emb, SG_EMBALAGEM: 'PK' };
                }
                return { ...emb };
              });

              const product = {
                ...item,
                PRECO,
                IMAGEM: '',
                EMBALAGEM,
                UNILEVER: item.FL_UNILEVER === 'True',
                ultimaCompra: productUltimaCompra[item.COD_PRODUTO] || {},
              } as ProductProps;

              const existingProduct = objProduct[item.COD_PRODUTO];
              if (!existingProduct) {
                products.push(product);
                objProduct[item.COD_PRODUTO] = product;
                return;
              }

              const mergedProduct = mergeProductsByCode(existingProduct, product);
              objProduct[item.COD_PRODUTO] = mergedProduct;

              const indexInProducts = products.findIndex((p) => p.COD_PRODUTO === item.COD_PRODUTO);
              if (indexInProducts >= 0) {
                products[indexInProducts] = mergedProduct;
              } else {
                products.push(mergedProduct);
              }
            });
            await yieldToMain();
          }
        }, 'catalogo');

        // Trabalhando com o dataset de categorias [categorias]
        await tryCatch(async () => {
          if (!!categorias?.length) {
            categorias?.forEach((item: CategoryProps) => {
              if (!objCategory[item?.COD_CATEGORIA]) {
                objCategory[item?.COD_CATEGORIA] = {
                  ...item,
                };
              }
            });
          }
        }, 'categorias');

        // Trabalhando com o dataset de categorias [allCategories]
        await tryCatch(async () => {
          isLoaded = !!categorias.find((c) => {
            return c.PATH_CATEGORIA === campanhaProduto?.url_amigavel;
          });
          allCategories = [...(isLoaded ? [] : mountCategories(campanhaProduto)), ...categorias];
        }, 'allCategories');

        // Trabalhando com o dataset de pagebuilder [listPageBuilder]
        await tryCatch(async () => {
          pageBuilder = listPageBuilder.map((i: any, ind: any) => ({ ...i, ID: toCoded(`${ind + 1}`, true, true) }));
        }, 'listPageBuilder');

        void registerEvent({
          tipoEvento: 'PROCESSAMENTO',
          obsEvento: 'Finalizando processamento de dados',
        }).catch(() => {});

        // dados | chave | deve validar | é obrigatório
        const dbWrites: [data: any, key: Tables, shouldValidate?: boolean, isObrigatory?: boolean][] = [
          [marcas, 'marcas', true, true],
          [precosSource, 'preco', true, true],
          [estoque, 'estoque', true, true],
          [imagens, 'imagens', true],
          [catalogo, 'catalogo', true, true],
          [Object.values(clientes), 'cliente', true, true],
          [products, 'produtos'],
          [saldoFlex, 'saldo_flex', true],
          [pageBuilder, 'pagebuilder', true],
          [indicadores, 'indicadores', true],
          [allCategories, 'categoria', true, true],
          [campanha, 'campanha_indicador', true],
          [parametrosAws, 'parametros_aws', true],
          [janelaEntrega, 'janela_entrega', true],
          [campanhaFaixa, 'campanha_faixa', true],
          [indicadorLoja, 'indicador_loja', true],
          [objProduct, 'dicionario_produtos'],
          [eventos, 'eventos'],
          [campanhaCliente, 'campanha_cliente', true],
          [campanhaProduto, 'campanha_produto', true],
          [objCategory, 'dicionario_categorias'],
          [promocaoCliente, 'precoPromocaoCliente', true],
          [campanhaTopMix, 'campanha_mix_fornecedor', true],
          [parametroPrecoBase, 'parametro_preco_base', true, true],
          [recomendacaoBigData, 'recomendacaoBigData', true],
          [percentualFornecedor, 'percentual_fornecedor', true, true],
          [substituicaotributaria, 'cod_regime_tributario', true, true],
          [cicloVendaCliente, 'ciclo_venda_cliente_cliente', true],
          [restricaoProdutoCliente, 'restricaoProdutoCliente', true],
          [ultimaCompra, 'ultima_compra', true],
          [clienteEventosCortes, 'cliente_cortes', true],
          [clienteBoletos, 'cliente_boletos', true],
          [combo, 'combo', true],
        ];

        const relatorio: { key: Tables; st: number; obs?: string }[] = [];
        const blockingProblems: InconsistencyProblem[] = [];
        let hasError = false;

        const typesAttemptedInRun = new Set(listToDownload.map((dataset) => normalizeDatasetType(dataset.TIPO_ARQUIVO)));

        const preparedMain: Array<{ name: Tables; data: any }> = [];
        let imagensPayload: any = null;

        for (const entry of dbWrites) {
          const [data, key, shouldValidate, isObrigatory] = entry;
          if (key === 'produtos') continue;

          const total = (Array.isArray(data) ? data.length : Object.keys(data || {}).length) || 0;

          if (shouldValidate && isObrigatory && total === 0) {
            if (wasTableAttemptedInRun(key, typesAttemptedInRun)) {
              const obs = `${OBRIGATORY_EMPTY_BLOCK_REASON} ${key}`;
              relatorio.push({ key, st: 0, obs });
              blockingProblems.push({ key, obs });
              hasError = true;
            }
            continue;
          }

          if (shouldValidate && !isObrigatory && total === 0) {
            relatorio.push({ key, st: 0, obs: 'sem dados {N}' });
            continue;
          }

          if (!shouldValidate && total === 0) {
            relatorio.push({ key, st: 0, obs: 'sem dados — escrita ignorada' });
            continue;
          }

          if (key === 'imagens') {
            imagensPayload = data;
            continue;
          }

          preparedMain.push({ name: key, data });
        }

        if (preparedMain.length > 0) {
          try {
            await setMultipleOnDBAtomic(preparedMain);
            preparedMain.forEach(({ name }) => relatorio.push({ key: name, st: 1 }));

            const savedTables = new Set(preparedMain.map(({ name }) => String(name)));
            const registeredMain = successfullyDownloadedMain.filter((dataset) => {
              const type = normalizeDatasetType(dataset.TIPO_ARQUIVO);
              const aliasedTable = Object.entries(TABLE_DATASET_TYPES).find(
                ([table, types]) => table === type || types.includes(type)
              )?.[0];
              const tableKey = aliasedTable || resolveTableNameForWrite(type) || type;
              return savedTables.has(tableKey);
            });
            await registerSuccessfulDownloads(registeredMain);
          } catch (err: any) {
            hasError = true;

            const msg = err?.message ?? String(err);
            const failedKey = extractAtomicFailureTableKey(msg) ?? preparedMain[0]?.name;
            if (failedKey) {
              blockingProblems.push({ key: failedKey, obs: msg });
            }
            preparedMain.forEach(({ name }) =>
              relatorio.push({ key: name, st: 0, obs: `falha na transação atômica IDB: ${msg}` })
            );
          }
        }

        if (hadImagensDownloadInThisRun && imagensPayload != null && isValidDbPayload(imagensPayload)) {
          try {
            await setImagesOnDB(imagensPayload);
            relatorio.push({ key: 'imagens', st: 1 });
            await registerSuccessfulDownloads(successfullyDownloadedImages);
          } catch (err: any) {
            relatorio.push({ key: 'imagens', st: 0, obs: err?.message ?? String(err) });
          }
        }

        void registerEvent({
          tipoEvento: 'DB_FUNCTIONS',
          obsEvento: `Salvando dados: ${relatorio
            .map((item) => {
              const obs = item.obs ? `[${item.obs}]` : '';
              return `${item.st} ${item.key} ${obs}`;
            })
            .join('|')}`,
          stEvento: hasError ? 0 : 1,
        }).catch(() => {});

        if (downloadLabelTimeoutRef.current) {
          clearTimeout(downloadLabelTimeoutRef.current);
          downloadLabelTimeoutRef.current = null;
        }

        const rcaCustomer = getOnStorage('rca_usuario');

        setCustomer(rcaCustomer as CustomerDataProps);
        setCampanhaProdutos(campanhaProduto || {});
        setCampanhaClientes(campanhaCliente || {});
        setCampanhaFaixa(campanhaFaixa || []);
        setCampanhaTopMix(campanhaTopMix || []);
        setIndicadorLoja(indicadorLoja || []);
        setIndicadoresProdutividade(indicadores || []);

        _useState.setPreparing(false);
        loading.current = false;
        
        if (hasError) {
          _useState.setError(true);
          _useState.setIsSuccess(false);
          console.warn('[RCA sync] Finalizado com erro ao gravar/processar dados');
          return undefined;
        }
        
        _useState.setIsSuccess(true);
        
        return true
      } finally {
        loading.current = false;
        if (!skipGlobalGuard) {
          globalSyncInProgress = false;
        }
      }
    },
    [
      _useState.fetchDatasets,
      _useState.setDownloadingFileName,
      getOnDB,
      setOnDB,
      setCustomer,
      setImagesOnDB,
      setCampanhaFaixa,
      setIndicadorLoja,
      setCampanhaTopMix,
      setCampanhaProdutos,
      setCampanhaClientes,
      setMultipleOnDBAtomic,
      setIndicadoresProdutividade,
    ]
  );

  return loadFiles;
};